/* eslint-disable no-magic-numbers */
import { VIF_ADD_SUB_DATA } from '../../games/data/vifAddSubData';
import {
  addSubPatternTable,
  buildProblemSet,
} from '../../games/engine/ProblemSource';

import type { ProblemPool } from './deal';
import { createRandomInt } from './rulesProblemState';

/**
 * Problem-pool selection for the 4x4 block add/sub board (`VIFAddSub`,
 * gameFlag 1=add, 2=sub). Direct port of `VIFAddSubGame.populateProblem`
 * (`games/VIFAddSub.ts:47-74`) with the engine's `this.randomPatterns` lifted
 * into an explicit `BlockAddSubProblemState`, mirroring
 * `rulesAddSubProblems.ts`.
 */

export type BlockAddSubFlag = 1 | 2;

const ADD: BlockAddSubFlag = 1;

/** Mutable pool-selection memory carried across games. */
export interface BlockAddSubProblemState {
  /** Legacy `randomPatterns`: -1 until rolled on layer 2's first visit, then held for the rest of the instance. */
  randomPatterns: number;
}

/**
 * Legacy keeps `randomPatterns` on the engine instance, not per game. The
 * layer here is the game number, so the roll made in game 2 has to survive
 * into games 3 and 4 - otherwise they would always resolve to group C.
 */
export function createBlockAddSubProblemState(
  previous?: BlockAddSubProblemState,
): BlockAddSubProblemState {
  return { randomPatterns: previous?.randomPatterns ?? -1 };
}

export interface PopulateArgs {
  flag: BlockAddSubFlag;
  /** Legacy `intLayer`, 1-based. */
  layer: number;
  /** Unused by add/sub; accepted for shape compatibility with `PopulateProblemsArgs`. */
  round: number;
  /** Unused by add/sub; accepted for shape compatibility with `PopulateProblemsArgs`. */
  columns: number;
  state: BlockAddSubProblemState;
  rng?: () => number;
  randomInt?: (min: number, max: number) => number;
}

/**
 * Returns the pool for the given flag/layer. Layer 1 always uses the tree's
 * base table; layers 2-4 roll a pattern group once (on layer 2's first visit)
 * and hold it - `addSubPatternTable` resolves which of the A/B/C tables that
 * roll plus the current layer selects.
 */
export function populateBlockAddSubProblems({
  flag,
  layer,
  state,
  rng = Math.random,
  randomInt = createRandomInt(rng),
}: PopulateArgs): ProblemPool {
  const tree = flag === ADD ? VIF_ADD_SUB_DATA.add : VIF_ADD_SUB_DATA.sub;

  if (layer === 1) {
    return buildProblemSet({ kind: 'table', ref: tree.layer1 });
  }

  if (layer === 2 && state.randomPatterns === -1) {
    state.randomPatterns = randomInt(0, 5);
  }

  return buildProblemSet({
    kind: 'table',
    ref: addSubPatternTable(tree, layer, state.randomPatterns),
  });
}
